import React from "react";
import GaugeChart from "react-gauge-chart";
import { formatNumberWithoutFraction } from "../utility";

interface GaugeChartProps {
  target?: number | any;
  served?: number | any;
  pending?: number | string;
  value?: number | string;
  labelValue?: any;
  showPercentage?: boolean;
}

const CustomGaugeChart: React.FC<GaugeChartProps> = ({
  served,
  pending,
  target,
  value = 0,
  labelValue,
  showPercentage = true, // Default to true for backward compatibility
}) => {
  const numericValue = isNaN(Number(value)) ? 0 : Number(value);
  const maxValue = Math.max(numericValue, 100); // Use 100 as minimum max value
  const percentage = showPercentage ? Math.min(numericValue, 1) : numericValue / maxValue;
  const levels = 50;

  const generateColors = (val: number, levels: number) => {
    if (isNaN(val) || val < 0 || levels <= 0) return ["#FF4D68"];
    const greenSegments = Math.max(0, Math.min(Math.round(val * levels), levels));
    const redSegments = levels - greenSegments;
    return [
      ...Array(greenSegments).fill("#00C48C"),
      ...Array(redSegments).fill("#FF4D68"),
    ];
  };

  const colors = generateColors(percentage, levels);

  const angle = Math.PI * percentage;
  const displayLabel = showPercentage 
    ? `${Math.round(percentage * 100)}%`
    : formatNumberWithoutFraction(labelValue || numericValue);
  
  // Calculate position as percentages for responsive layout
  const radiusPercent = 50; // Center of the gauge
  const offsetPercent = Math.round(percentage * 100) > 50 ? 52 : 52; // Distance from center as percentage
  
  const leftPercent = radiusPercent + offsetPercent * Math.cos(angle - Math.PI);
  const topPercent = radiusPercent + offsetPercent * Math.sin(angle - Math.PI);

  return (
    <div className="relative w-full h-full">
      <GaugeChart
        id="gauge-chart"
        nrOfLevels={levels}
        percent={percentage}
        arcPadding={0.02}
        cornerRadius={3}
        needleColor="#345243"
        needleBaseColor="#464A4F"
        animate={false}
        textColor="black"
        hideText={true}
        colors={colors}
      />
      <div
        className="absolute text-sm font-semibold px-2 py-1 rounded text-center whitespace-nowrap"
        style={{
          left: `${leftPercent}%`,
          top: `${topPercent}%`,
          transform: "translate(-50%, -50%)",
        }}
      >
        {displayLabel}
      </div>
    </div>
  );
};

export default CustomGaugeChart;
